import React, { useState, useEffect, useRef } from 'react';
import { Card, Button, Space, Spin, message, Select, Modal } from 'antd';
import { ZoomInOutlined, ZoomOutOutlined, CompressOutlined, ExpandOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { API_BASE_URL } from '../../utils/config';
import axios from 'axios';
import mermaid from 'mermaid';
import FormikForm from '../form/FormikForm';
import './OrganizasyonSemasiPage.css';

const { Option } = Select;

const OrganizasyonSemasiPage = () => {
  const { darkMode } = useTheme();
  const { getAuthHeaders } = useAuth();
  const { semaId } = useParams();
  const navigate = useNavigate();
  const [semalar, setSemalar] = useState([]);
  const [selectedSema, setSelectedSema] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [diagramLoading, setDiagramLoading] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [addPersonnelModalVisible, setAddPersonnelModalVisible] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);  
  const [editFormId] = useState(29); 
  const [addPersonnelFormId] = useState(30); 
  const mermaidRef = useRef(null);
  const zoomContainerRef = useRef(null);
  const nodeMapRef = useRef(new Map());

  const firmaColors = {
    1: { border: '#a40909', bg: '#f4c0c0', name: 'ATMPoint' },
    2: { border: '#09a461', bg: '#a4edcd', name: 'Masef' },
    3: { border: '#262e5d', bg: '#a4c6ed', name: 'ATM İstanbul' },
    4: { border: '#4b690b', bg: '#d3f58b', name: 'Reaktif Beton' },
    9: { border: '#9f9606', bg: '#f1eca4', name: 'Aktif Etkinlik' },
    10: { border: '#084a63', bg: '#bbedff', name: 'ATM Kiralama' }
  };

  const optimizedStyles = `
    .n-c { display:flex; flex-direction:column; align-items:center; padding:4px; min-width:100px; text-align:center; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial; }
    .n-h { margin-bottom:4px; display:flex; justify-content:center; }
    .n-i { width:28px; height:28px; border-radius:50%; object-fit:cover; border:2px solid #fff; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
    .n-init { width:28px; height:28px; border-radius:50%; background:#e0e0e0; color:#555; font-weight:bold; display:flex; align-items:center; justify-content:center; border:2px solid #fff; font-size:11px; }
    .n-b { line-height:1.2; width: 100%; }
    .n-t { font-weight:bold; font-size:11px; color:#333; margin-bottom:2px; }
    .n-s { font-size:9px; color:#666; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; max-width: 140px; margin: 0 auto; }
    .n-a { display:flex; gap:4px; margin-top:4px; justify-content: center; }
    .btn { width:18px; height:18px; background:#fff; border:1px solid #ccc; border-radius:3px; cursor:pointer; font-size:10px; display:flex; align-items:center; justify-content:center; transition: all 0.2s; }
    .btn:hover { background:#eee; transform: scale(1.1); }
    /* Mermaid Node Stil Override */
    .node rect, .node polygon, .node circle { fill: inherit !important; }
  `;

  const generateUserPhotoUrl = (userId) => {
    if (!userId) return null;
    return `https://dosya.atmturkiye.com/atmpoint/personel%2F${userId}.jpg`;
  };

  useEffect(() => {
    loadSemalar();
  }, []);

  useEffect(() => {
    if (semaId && semalar.length > 0) {
      const semaIdNum = parseInt(semaId, 10);
      if (!isNaN(semaIdNum) && selectedSema !== semaIdNum) {
        setSelectedSema(semaIdNum);
      }
    }
  }, [semaId, semalar]);

  useEffect(() => {
    if (selectedSema) {
      loadOrganizasyonData(selectedSema);
      if (semaId !== selectedSema.toString()) {
        navigate(`/organizasyon-semasi/${selectedSema}`, { replace: true });
      }
    }
  }, [selectedSema]);

  useEffect(() => {
    if (nodes.length > 0) {
      renderMermaidDiagram();
    }
  }, [nodes, darkMode]);

  useEffect(() => {
    const container = zoomContainerRef.current;
    if (!container) return;

    const wheelHandler = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setZoomLevel(prev => Math.max(0.2, Math.min(4, prev + delta)));
    };

    container.addEventListener('wheel', wheelHandler, { passive: false });
    return () => container.removeEventListener('wheel', wheelHandler);
  }, []);

  const loadSemalar = async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();
      const response = await axios.get(`${API_BASE_URL}/organizasyon/semalar`, { headers });
      if (response.data.success) {
        setSemalar(response.data.semalar);
        if (!semaId) {
            const defaultSema = response.data.semalar.find(s => s.id === 36) || response.data.semalar[0];
            if (defaultSema) setSelectedSema(defaultSema.id);
        }
      }
    } catch (error) {
      console.error(error);
      message.error('Semalar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const loadOrganizasyonData = async (id) => {
    try {
      setDiagramLoading(true);
      const headers = getAuthHeaders();
      const response = await axios.get(`${API_BASE_URL}/organizasyon/sema/${id}`, { headers });
      if (response.data.success) {
        setNodes(response.data.nodes);
      }
    } catch (error) {
      console.error(error);
      message.error('Veri yüklenemedi');
    } finally {
      setDiagramLoading(false);
    }
  };

  const buildMermaidDiagram = (nodeList) => {
    if (!nodeList || nodeList.length === 0) return 'graph TD\n A[Veri Yok]';

    let diagram = 'graph TD\n';
    nodeMapRef.current.clear();
    let nodeCounter = 0;
    const usedFirmas = new Set();
    const collectFirmas = (list) => {
        list.forEach(n => {
            usedFirmas.add(n.firma || 0);
            if(n.children) collectFirmas(n.children);
        });
    };
    collectFirmas(nodeList);

    usedFirmas.forEach(firmaId => {
        const colors = firmaColors[firmaId] || { border: '#999', bg: '#fff' };
        diagram += `    classDef f${firmaId} fill:${colors.bg},stroke:${colors.border},stroke-width:1px\n`;
    });

    const traverseChildren = (list, parentUniqueId) => {
        list.forEach(node => {
            const uniqueId = `N${nodeCounter++}`;
            nodeMapRef.current.set(uniqueId, node);

            const ad = (node.ad || '').replace(/"/g, "'");
            const pozisyon = (node.pozisyon || '').replace(/"/g, "'");
            const imgUrl = generateUserPhotoUrl(node.no);
            const firmaId = node.firma || 0;
            const init = ad ? ad.charAt(0).toUpperCase() : '?';
            const imgHtml = imgUrl 
                ? `<img src='${imgUrl}' class='n-i' onerror='this.style.display="none";this.nextSibling.style.display="flex"'/>` 
                : '';
            
            const initHtml = `<div class='n-init' style='display:${imgUrl ? 'none' : 'flex'}'>${init}</div>`;
            let label = `
<div class='n-c'>
<div class='n-h'>${imgHtml}${initHtml}</div>
<div class='n-b'><div class='n-t'>${ad}</div><div class='n-s'>${pozisyon}</div></div>
<div class='n-a'>
<div class='btn edit-btn' data-id='${uniqueId}' title='Düzenle'>✏️</div>
<div class='btn add-btn' data-id='${uniqueId}' title='Ekle'>➕</div>
</div>
</div>`;

            label = label.replace(/\n/g, '').replace(/\s\s+/g, ' ');

            diagram += `    ${uniqueId}["${label}"]\n`;
            diagram += `    class ${uniqueId} f${firmaId}\n`;

            if (parentUniqueId) {
                diagram += `    ${parentUniqueId} --> ${uniqueId}\n`;
            }

            if (node.children) traverseChildren(node.children, uniqueId);
        });
    };

    traverseChildren(nodeList, null);
    return diagram;
  };

  const renderMermaidDiagram = async () => {
    if (!mermaidRef.current) return;
    setDiagramLoading(true);

    try {
      mermaid.initialize({
        startOnLoad: false,
        theme: darkMode ? 'dark' : 'base',
        securityLevel: 'loose',
        maxTextSize: 1000000, 
        flowchart: {
          useMaxWidth: false,
          htmlLabels: true,
          curve: 'basis',
          padding: 15
        }
      });

      const diagramDefinition = buildMermaidDiagram(nodes);
      
      const id = `mermaid-${Date.now()}`;
      mermaidRef.current.innerHTML = '';
      
      const { svg } = await mermaid.render(id, diagramDefinition);
      mermaidRef.current.innerHTML = svg;

      setTimeout(() => {
        addClickHandlersToNodeButtons();
      }, 300);

      setZoomLevel(1);
      setPanPosition({ x: 0, y: 0 });

    } catch (error) {
      console.error('Mermaid render hatası:', error);
    } finally {
      setDiagramLoading(false);
    }
  };

  const addClickHandlersToNodeButtons = () => {
    if (!mermaidRef.current) return;
    
    const editBtns = mermaidRef.current.querySelectorAll('.edit-btn');
    editBtns.forEach(btn => {
        btn.onclick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            const uId = btn.getAttribute('data-id');
            const nodeData = nodeMapRef.current.get(uId);
            if (nodeData) {
                setSelectedNode(nodeData);
                setEditModalVisible(true);
            }
        };
    });

    const addBtns = mermaidRef.current.querySelectorAll('.add-btn');
    addBtns.forEach(btn => {
        btn.onclick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            const uId = btn.getAttribute('data-id');
            const nodeData = nodeMapRef.current.get(uId);
            if (nodeData) {
                setSelectedNode(nodeData);
                setAddPersonnelModalVisible(true);
            }
        };
    });
  };

  const handleMouseDown = (e) => {
    if (e.button === 0) {
      setIsPanning(true);
      setPanStart({ x: e.clientX - panPosition.x, y: e.clientY - panPosition.y });
    }
  };

  const handleMouseMove = (e) => {
    if (isPanning) {
      setPanPosition({
        x: e.clientX - panStart.x,
        y: e.clientY - panStart.y
      });
    }
  };

  const handleMouseUp = () => setIsPanning(false);
  const handleZoomIn = () => setZoomLevel(p => Math.min(p + 0.2, 4));
  const handleZoomOut = () => setZoomLevel(p => Math.max(p - 0.2, 0.2));
  const handleReset = () => { setZoomLevel(1); setPanPosition({x:0,y:0}); };

  const handleEditFormSuccess = () => {
    message.success('Başarıyla güncellendi');
    setEditModalVisible(false);
    loadOrganizasyonData(selectedSema);
  };

  const handleAddPersonnelFormSuccess = () => {
    message.success('Başarıyla eklendi');
    setAddPersonnelModalVisible(false);
    loadOrganizasyonData(selectedSema);
  };

  if (loading) return (
    <div style={{ display:'flex', justifyContent:'center', marginTop: 100 }}>
        <Spin size="large" />
    </div>
  );

  return (
    <div className={`organizasyon-page ${darkMode ? 'dark' : 'light'}`}>
      <style>{optimizedStyles}</style>
      <Card>
        <Space direction="vertical" style={{ width: '100%' }}>
            
            {/* Üst Bar */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                <Space>
                    <span style={{ fontWeight: 600 }}>Organizasyon Şeması:</span>
                    <Select value={selectedSema} onChange={setSelectedSema} style={{ width: 220 }}>
                        {semalar.map(s => <Option key={s.id} value={s.id}>{s.ad}</Option>)}
                    </Select>
                </Space>
                <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                    {Object.entries(firmaColors).map(([id, col]) => (
                        <span key={id} style={{ 
                            fontSize: '10px', 
                            padding: '3px 8px', 
                            background: col.bg, 
                            border: `1px solid ${col.border}`,
                            color: col.border,
                            borderRadius: '4px',
                            fontWeight: 500
                        }}>
                            {col.name}
                        </span>
                    ))}
                </div>
            </div>

            {/* Diyagram Alanı */}
            <div style={{ 
                position: 'relative', 
                border: '1px solid #d9d9d9', 
                borderRadius: '8px', 
                overflow: 'hidden', 
                height: '650px',
                background: darkMode ? '#141414' : '#fafafa'
            }}>
                
                {/* Araçlar */}
                <div style={{ 
                    position: 'absolute', top: 15, right: 15, zIndex: 100, 
                    background: darkMode ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.9)', 
                    padding: '6px', borderRadius: '6px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                }}>
                    <Space.Compact>
                        <Button icon={<ZoomInOutlined />} onClick={handleZoomIn} />
                        <Button icon={<ZoomOutOutlined />} onClick={handleZoomOut} />
                        <Button icon={<CompressOutlined />} onClick={handleReset} />
                    </Space.Compact>
                    <div style={{ textAlign: 'center', fontSize: '10px', marginTop: 4, color: '#888' }}>
                        {Math.round(zoomLevel * 100)}%
                    </div>
                </div>

                {diagramLoading && (
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.7)', zIndex: 50 }}>
                        <Spin size="large" tip="Şema Çiziliyor..." />
                    </div>
                )}

                <div
                    ref={zoomContainerRef}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    style={{
                        width: '100%',
                        height: '100%',
                        cursor: isPanning ? 'grabbing' : 'grab',
                        transform: `translate3d(0,0,0)` 
                    }}
                >
                    <div 
                        ref={mermaidRef} 
                        style={{
                            transform: `translate(${panPosition.x}px, ${panPosition.y}px) scale(${zoomLevel})`,
                            transformOrigin: '0 0',
                            transition: isPanning ? 'none' : 'transform 0.05s linear',
                            padding: '40px',
                            minHeight: '100%',
                            minWidth: '100%'
                        }}
                    />
                </div>
            </div>
        </Space>
      </Card>

      {/* Modallar */}
      <Modal 
        title="Personel Düzenle" 
        open={editModalVisible} 
        onCancel={() => setEditModalVisible(false)} 
        footer={null} 
        destroyOnHidden
        width={700}
      >
        {selectedNode && (
            <FormikForm 
                formId={editFormId} 
                uniqueValue={selectedNode.id} 
                uniqueField="id" 
                onSuccess={handleEditFormSuccess} 
                initialValues={selectedNode}
                extraData={selectedNode}
            />
        )}
      </Modal>

      <Modal 
        title="Yeni Bağlı Pozisyon Ekle" 
        open={addPersonnelModalVisible} 
        onCancel={() => setAddPersonnelModalVisible(false)} 
        footer={null} 
        destroyOnHidden
        width={700}
      >
        {selectedNode && (
            <FormikForm 
                formId={addPersonnelFormId} 
                onSuccess={handleAddPersonnelFormSuccess} 
                initialValues={{ parent_id: selectedNode.id, firma: selectedNode.firma }} 
                extraData={{ parent_id: selectedNode.id, firma: selectedNode.firma }}
            />
        )}
      </Modal>
    </div>
  );
};

export default OrganizasyonSemasiPage;